import { normalizePhone, phoneSearchDigits, type ClientListItem } from '@kominiarz/shared';
import type { ReactNode } from 'react';
import { useClientSearch, useDebounced } from '../lib/clientsApi';
import { Alert } from './Alert';
import styles from './SimilarClients.module.css';

interface SimilarClientsProps {
  name: string;
  phone: string;
  /** What to do with a match: open it, or use it instead of adding a new client. */
  action: (client: ClientListItem) => ReactNode;
}

/**
 * While a new client is being typed in: clients already saved with that phone number
 * (or, without one, a similar name), so the same person is not added twice.
 */
export function SimilarClients({ name, phone, action }: SimilarClientsProps) {
  const digits = phoneSearchDigits(normalizePhone(phone) ?? '');
  const typedName = name.trim();
  // A full number is the surest match; without one, a name of three letters or more.
  const q = useDebounced(digits.length >= 11 ? digits.slice(2) : typedName.length >= 3 ? typedName : '', 400);
  const search = useClientSearch(q, { enabled: q.length > 0, limit: 3 });
  const found = q ? (search.data?.clients ?? []) : [];
  if (found.length === 0) return null;
  return (
    <Alert kind="info" title="Ten klient może już być w bazie">
      <ul className={styles.list}>
        {found.map((c) => (
          <li key={c.id} className={styles.item}>
            <span className={styles.text}>
              <strong>{c.name ?? c.phones[0]?.phone ?? 'Klient'}</strong>
              <span className={styles.meta}>{[c.name ? c.phones[0]?.phone : null, c.objects[0]?.address].filter(Boolean).join(', ')}</span>
            </span>
            {action(c)}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
